import type { PracticeDifficulty } from "common/practiceQuestionTemplates";
import type { PracticeSubject } from "common/practiceSubjects";
import { PRACTICE_QUESTION_TEMPLATES } from "common/practiceQuestionTemplates";
import { localizePracticeTemplate } from "common/practiceTemplateLocale";
import {
  BOARD_ROLE_BANNERS,
  TEACHER_WORKFLOW_STEPS,
  WELCOME_PATHS,
} from "../constants/helpCopy";

const SUBJECT_HE: Record<PracticeSubject, string> = {
  Algebra: "אלגברה",
  Geometry: "גאומטריה",
  Trigonometry: "טריגונומטריה",
  Calculus: "חדו״א",
  Statistics: "סטטיסטיקה",
  Vectors: "וקטורים",
  Complex: "מרוכבים",
  General: "מספרים",
};

const DIFFICULTY_HE: Record<PracticeDifficulty, string> = {
  intro: "מבוא",
  core: "ליבה",
  challenge: "אתגר",
};

const TAG_HE: Record<string, string> = {
  linear: "קו ישר",
  "one-step": "שלב אחד",
  quadratic: "ריבועית",
  factoring: "פירוק לגורמים",
  systems: "מערכת",
  vertex: "קודקוד",
  parabola: "פרבולה",
  area: "שטח",
  triangle: "משולש",
  pythagorean: "פיתגורס",
  "right-triangle": "ישר-זווית",
  circle: "מעגל",
  circumference: "היקף",
  "exact-values": "ערכים מדויקים",
  "unit-circle": "מעגל היחידה",
  "trig-ratios": "יחסים טריגונומטריים",
  derivative: "נגזרת",
  "power-rule": "כלל החזקה",
  integral: "אינטגרל",
  "indefinite-integral": "אינטגרל לא מסוים",
  "chain-rule": "כלל השרשרת",
  mean: "ממוצע",
  average: "ממוצע",
  probability: "הסתברות",
  independent: "בלתי תלויים",
  median: "חציון",
  center: "מרכז",
  pemdas: "סדר פעולות",
  arithmetic: "חשבון",
  percent: "אחוזים",
  "word-problem": "מילולית",
  fractions: "שברים",
  inequality: "אי-שוויון",
  expand: "פתיחת סוגריים",
  binomial: "בינום",
  slope: "שיפוע",
  angles: "זוויות",
  similar: "דמיון",
  proportion: "פרופורציה",
  tangent: "טנגנס",
  inverse: "הפוך",
  "special-angles": "זוויות מיוחדות",
  "product-rule": "כלל המכפלה",
  "definite-integral": "אינטגרל מסוים",
  range: "טווח",
  spread: "פיזור",
  mode: "שכיח",
  "sample-space": "מרחב מדגם",
  exponents: "חזקות",
  ratio: "יחס",
  vectors: "וקטורים",
  components: "רכיבים",
  magnitude: "גודל",
  scalar: "סקלר",
  "dot-product": "מכפלה סקלרית",
  imaginary: "מדומה",
  "powers-of-i": "חזקות של i",
  complex: "מרוכבים",
  modulus: "מודולוס",
  rectangle: "מלבן",
  perimeter: "היקף",
  volume: "נפח",
  "square-root": "שורש",
  intercept: "חותך",
  distance: "מרחק",
  trapezoid: "טרפז",
  midpoint: "אמצע",
  "unit-price": "מחיר ליחידה",
  discount: "הנחה",
  complement: "משלים",
  combinations: "צירופים",
  frequency: "שכיחות",
  "weighted-mean": "ממוצע משוקלל",
};

const CRUMB_HE: Record<string, string> = {
  Practice: "תרגול",
  "Pick a subject and question": "בחרו רמה, נושא ושאלה",
  "Pick a level and subject": "בחרו רמה ונושא",
  "Blank sheet": "דף ריק",
  "Write or paste question": "כתבו או הדביקו שאלה",
  Question: "שאלה",
};

const nameToHebrew = new Map(
  PRACTICE_QUESTION_TEMPLATES.map((template) => [
    template.name,
    localizePracticeTemplate(template, "he").name,
  ]),
);

export function subjectLabel(subject: string, he: boolean): string {
  if (subject === "General") return he ? SUBJECT_HE.General : "Numbers";
  if (!he) return subject;
  return SUBJECT_HE[subject as PracticeSubject] ?? subject;
}

export function difficultyLabel(
  difficulty: PracticeDifficulty,
  he: boolean,
): string {
  if (!he) {
    if (difficulty === "intro") return "Intro";
    if (difficulty === "core") return "Core";
    return "Challenge";
  }
  return DIFFICULTY_HE[difficulty];
}

export function tagLabel(tag: string, he: boolean): string {
  if (!he) return tag;
  return TAG_HE[tag] ?? tag;
}

/** English board labels stored on the context, shown in Hebrew on practice routes. */
export function boardChromeText(text: string, he: boolean): string {
  if (!he) return text;
  return CRUMB_HE[text] ?? SUBJECT_HE[text as PracticeSubject] ?? nameToHebrew.get(text) ?? text;
}

export function welcomePractice(he: boolean, signedIn = false) {
  const guest = he
    ? {
        title: "מורה בינה מלאכותית",
        blurb:
          "תרגול עצמאי לפי רמה ונושא — כותבים על הלוח, בודקים תשובות, או מתרגלים עד בקיאות. העבודה נשארת במכשיר הזה.",
        cta: "תרגול לפי רמה",
        ctaGuest: "תרגול לפי רמה",
        blankCta: "דף ריק (כתיבה, הדבקה או העלאה)",
        tourCta: "צפו בסיור של 40 שניות",
        toolsTourCta: "סימנים וסרגל הכלים",
        landingLead:
          "בחרו רמה ואז נושא — כותבים על הלוח, בודקים תשובות, או מתרגלים עד בקיאות. ההתקדמות נשמרת בחשבון כשמחוברים, ובדפדפן לאורחים.",
        classroomLink: "לפתוח או להצטרף לשיעור אונליין",
      }
    : WELCOME_PATHS.practice;
  if (!signedIn) return guest;
  return {
    ...guest,
    blurb: he
      ? "תרגול עצמאי לפי רמה ונושא — כותבים על הלוח, בודקים תשובות, או מתרגלים עד בקיאות. ההתקדמות נשמרת בחשבון."
      : "Solo practice by level and subject — write on the board, check answers, or follow a path until you are proficient. Progress is saved to your account.",
    landingLead: he
      ? "בחרו רמה ואז נושא — כותבים על הלוח, בודקים תשובות, או מתרגלים עד בקיאות. ההתקדמות נשמרת בחשבון."
      : "Choose a level, then a subject — write on the board, check answers, or follow a path until you are proficient. Progress is saved to your account.",
  };
}

export function practiceBanner(
  key: "practiceStudent" | "practiceBlank" | "practiceGuest",
  he: boolean,
): string {
  if (!he) return BOARD_ROLE_BANNERS[key];
  if (key === "practiceBlank") {
    return "כתבו או הדביקו את השאלה, ואז פתרו. בדיקת תשובה מדרגת כשמבקשים; רמזים ודיבור מוסיפים עזרה תוך כדי כתיבה. העבודה נשארת במכשיר הזה.";
  }
  if (key === "practiceGuest") {
    return "תרגול אורח — בדיקת תשובה מדרגת כשמבקשים; רמזים ודיבור מוסיפים עזרה תוך כדי כתיבה. מספר שימושי בינה מלאכותית חינמיים ביום; התחברו למכסה גבוהה יותר. העבודה נשארת במכשיר הזה.";
  }
  return "בדיקת תשובה מדרגת את העבודה כשמבקשים. רמזים ודיבור נותנים עזרה תוך כדי כתיבה — גם בתוך תיבת טקסט אחרי הפסקה. ההתקדמות נשמרת בחשבון.";
}

export function practiceRouteSeo(
  name: string,
  he: boolean,
): { title: string; description: string; keywords: string } | null {
  if (!he) return null;
  if (name === "practice" || name === "practiceHe") {
    return {
      title: "מורה למתמטיקה — תרגול על לוח",
      description:
        "תרגול מתמטיקה לבד על לוח אינטראקטיבי. בוחרים נושא או מתחילים מדף ריק, בודקים תשובות, ומקבלים רמזים או דיבור. ההתקדמות נשמרת בחשבון כשמחוברים, ובדפדפן לאורחים.",
      keywords:
        "תרגול מתמטיקה, מורה בינה מלאכותית, לוח מתמטיקה, בדיקת תשובות, תרגול עצמאי",
    };
  }
  if (name === "practiceBlank") {
    return {
      title: "דף תרגול ריק — מורה למתמטיקה",
      description:
        "תרגול על לוח ריק עם מורה בינה מלאכותית: כותבים או מדביקים שאלה, או מעלים תמונה של דף עבודה. העבודה נשארת במכשיר.",
      keywords: "דף ריק, תרגול מתמטיקה, מורה בינה מלאכותית",
    };
  }
  if (name === "practiceQuestion") {
    return {
      title: "תרגול — מורה למתמטיקה",
      description: "עבודה על שאלת תרגול עם טיוטה מקומית ורמזים מהמורה.",
      keywords: "תרגול מתמטיקה, מורה בינה מלאכותית",
    };
  }
  return null;
}

export function practiceImageTip(
  kind: "unreadable" | "misread",
  he: boolean,
): string {
  if (kind === "unreadable") {
    return he
      ? "לא הצלחתי לקרוא את התמונה בבירור. הדביקו את השאלה כטקסט (Ctrl+V), או השתמשו בתמונה גדולה יותר עם כתב כהה על רקע בהיר."
      : "I couldn't read that image clearly. Paste the question as text (Ctrl+V), or use a larger photo with dark writing on a light background.";
  }
  return he
    ? "ייתכן שקראתי את הדף לא נכון. הדביקו את השאלה כטקסט (Ctrl+V) כדי שאוכל לעקוב אחרי השאלה עצמה."
    : "I may have misread the worksheet. Paste the question as text (Ctrl+V) so I can follow the actual problem.";
}

export function lockedPartMessage(
  firstId: string,
  targetId: string,
  he: boolean,
): string {
  return he
    ? `התחילו ב-(${firstId}) לפני (${targetId}).`
    : `Start (${firstId}) before (${targetId}).`;
}

const UI_EN = {
  aiHelp: "AI help",
  gradeWhenYouAsk: "Grade when you ask",
  onDemandAria: "On demand — grade when you ask",
  onDemand: "On demand",
  tipsWhileYouWrite: "Tips while you write",
  tipsAria: "Tips — written tips while you write",
  tips: "Tips",
  spokenTips: "Spoken tips while you write",
  speakAria: "Speak — spoken tips while you write",
  speak: "Speak",
  checkAnswer: "Check answer",
  checkAnswerAria: "Check answer",
  checkAnswerShortcutAria: "Check answer, Control Enter",
  shareBoard: "Share board",
  shareBoardAria: "Share this practice board",
  linkCopied: "Link copied",
  workingTogether: "Working together on this board",
  shareFailed: "Couldn't share this board",
  shareSeatTaken: "Someone is already working on this board",
  hint: "Hint",
  hintsUsed: "Hints used",
  nextHint: "Next hint",
  resumeTips: "Resume tips",
  pauseTips: "Pause tips",
  signInHigher: "Sign in for a higher limit",
  aiTutorQuota: "AI tutor quota",
  dailyLimit: "Daily AI limit reached",
  aiUnavailable: "AI tutor unavailable",
  loadFailedTitle: "Couldn't load this practice question",
  backToPractice: "Back to practice",
  wrongProblem: "Wrong problem?",
  nextQuestion: "Next question",
  moreLikeThis: "More like this",
  clearTitle: "Clear this problem?",
  clearBody:
    "The question and your writing on the board will be removed. You can paste a new problem after that.",
  keepProblem: "Keep problem",
  clearProblem: "Clear problem",
  dailyLimitReason: "Daily AI limit reached",
  pasteFirst: "Paste the problem first",
  loadingQuestion: "Loading question",
  pasteTheProblem: "Paste the problem",
  tipsPaused: "Tips paused",
  thinking: "Thinking…",
  speakingAfterPause: "Speaking tips after you pause",
  tipsAfterPause: "Tips after you pause",
  gettingStarted: "Getting started",
  worksheetImage: "Worksheet image",
  coach: "Coach",
  askTutor: "Ask",
  askPlaceholder: "Ask about this problem…",
  askAria: "Ask the tutor a question",
  tutorAnswer: "Answer",
  correctWithNote: "Correct, with a note",
  correct: "Correct",
  notQuite: "Not quite",
  checkFailed: "Practice check failed",
  usedToday: "Used",
  ofToday: "of",
  aiUsesToday: "AI uses today (resets at midnight UTC).",
  guest: "Guest",
  signedIn: "Signed-in",
  aiUsesLeftToday: "AI uses left today. Resets at midnight UTC.",
  guestModePrefix: "Guest mode:",
  freeUsesPerDay: "free AI uses per day. Sign in for",
  perDay: "/day.",
  aiUsesPerDay: "AI uses per day.",
  aiUseLeft: "AI use left",
  aiUsesLeft: "AI uses left",
  guestAiUses: "guest AI uses / day",
  ctrlEnter: "Ctrl+Enter",
  listTitle: "Practice",
  blankSheet: "Blank sheet",
  blankSheetBody:
    "Write the question on the board, paste it as text (Ctrl+V), or paste/upload a worksheet image, then solve.",
  questionHeader: "Question",
  difficultyHeader: "Difficulty",
  loadingQuestions: "Loading questions…",
  noQuestions: "No practice questions in this subject yet.",
  chooseLevel: "Choose a level",
  chooseSubject: "Choose a subject",
  chooseExercise: "Choose an exercise",
  gradeFilter: "Grade",
  allGrades: "All",
  gradeEmpty: "No exercises for this grade yet.",
  newProblem: "New problem",
  newProblemFailed: "Could not start a new problem.",
  relatedPractice: "Related practice",
  relatedPracticeFailed: "Could not start a related exercise.",
  stepDown: "Three misses. Here is a worked example, then an easier problem.",
  stepPrereq:
    "Three misses. Here is a worked example of an earlier skill, then a new problem.",
  stepUp: "Three in a row. The next problem is a step harder.",
  generatorFailed: "Could not start that exercise.",
  practiceThisSubject: "Practice this subject",
  curriculumLabel: "Curriculum",
  backToLevels: "All levels",
  backToSubjects: "All subjects",
  emptyBand:
    "No catalog exercises at this level yet. Start a guided path and we will generate drills.",
  pathPercent: (pct: number) => `${pct}% proficient`,
  subjectProficient: "Subject proficient",
  chooseAnotherSubject: "Choose another subject",
  pathNext: "Next in path",
  generatedPathStem: (skill: string, subject: string) =>
    `Practice path: ${skill || subject}`,
  pasteTheProblemTitle: "Paste the problem",
  pasteTheProblemHelp: "Ctrl+V for text or a worksheet image, or upload a picture.",
  pastePlaceholder: "Or type the question here…",
  start: "Start",
  clear: "Clear",
  uploadImage: "Upload image",
  readingWorksheet: "Reading the worksheet…",
  loadingQuestionPane: "Loading question…",
  worksheetAlt: "Worksheet",
  problem: "Problem",
  hintFor: "Hint for",
  animatedExplanation: "Animated explanation",
  animatedExplanationHelp:
    "A short reminder of this skill. It uses different numbers than your problem.",
  watchExplanation: "View explanation",
  skillReminder: "How this works",
  skillReminderExample: "Example",
  showShortExample: "Show a short example",
  loadingShortExample: "Writing an example…",
  dismissTip: "Dismiss tip",
  dragToMove: "Drag to move",
  practiceTooltip: "Practice",
  help: "Help",
  changeProblem: "Change problem",
  orderTasks: "Start tasks in order. Enter adds space in the current task.",
  guestDailyLimit: (limit: number) =>
    `You've used your ${limit} free AI uses for today. Sign in for a higher daily limit.`,
  memberDailyLimit: (limit: number) =>
    `Daily AI limit reached (${limit} uses). Try again tomorrow.`,
  tutorFailed: "AI tutor failed. Please try again.",
  imageUploadFailed: "Image upload failed",
  questionMissing: "This practice question does not exist.",
  notPracticeQuestion: "This is not a practice question.",
  loadFailed: "Couldn't load this practice question.",
};

const UI_HE: typeof UI_EN = {
  aiHelp: "עזרת בינה",
  gradeWhenYouAsk: "דירוג כשמבקשים",
  onDemandAria: "לפי בקשה — דירוג כשמבקשים",
  onDemand: "לפי בקשה",
  tipsWhileYouWrite: "רמזים תוך כדי כתיבה",
  tipsAria: "רמזים — רמזים כתובים תוך כדי כתיבה",
  tips: "רמזים",
  spokenTips: "רמזים מדוברים תוך כדי כתיבה",
  speakAria: "דיבור — רמזים מדוברים תוך כדי כתיבה",
  speak: "דיבור",
  checkAnswer: "בדיקת תשובה",
  checkAnswerAria: "בדיקת תשובה",
  checkAnswerShortcutAria: "בדיקת תשובה, קונטרול אנטר",
  shareBoard: "שיתוף הלוח",
  shareBoardAria: "שיתוף לוח התרגול",
  linkCopied: "הקישור הועתק",
  workingTogether: "עובדים יחד על הלוח",
  shareFailed: "לא הצלחנו לשתף את הלוח",
  shareSeatTaken: "מישהו כבר עובד על הלוח הזה",
  hint: "רמז",
  hintsUsed: "הרמזים נוצלו",
  nextHint: "הרמז הבא",
  resumeTips: "המשך רמזים",
  pauseTips: "השהיית רמזים",
  signInHigher: "התחברו למכסה גבוהה יותר",
  aiTutorQuota: "מכסת המורה",
  dailyLimit: "הגעתם למכסה היומית",
  aiUnavailable: "המורה אינו זמין",
  loadFailedTitle: "לא הצלחנו לטעון את שאלת התרגול",
  backToPractice: "חזרה לתרגול",
  wrongProblem: "שאלה לא נכונה?",
  nextQuestion: "השאלה הבאה",
  moreLikeThis: "עוד תרגילים",
  clearTitle: "לנקות את השאלה?",
  clearBody: "השאלה והכתיבה על הלוח יוסרו. אחר כך אפשר להדביק שאלה חדשה.",
  keepProblem: "השאירו את השאלה",
  clearProblem: "נקו את השאלה",
  dailyLimitReason: "הגעתם למכסה היומית",
  pasteFirst: "הדביקו קודם את השאלה",
  loadingQuestion: "טוען שאלה",
  pasteTheProblem: "הדביקו את השאלה",
  tipsPaused: "הרמזים מושהים",
  thinking: "חושב…",
  speakingAfterPause: "רמזים מדוברים אחרי הפסקה",
  tipsAfterPause: "רמזים אחרי הפסקה",
  gettingStarted: "מתחילים",
  worksheetImage: "תמונת דף עבודה",
  coach: "מורה",
  askTutor: "שאלו",
  askPlaceholder: "שאלו על השאלה הזו…",
  askAria: "שאלו את המורה",
  tutorAnswer: "תשובה",
  correctWithNote: "נכון, עם הערה",
  correct: "נכון",
  notQuite: "עדיין לא",
  checkFailed: "בדיקת התרגול נכשלה",
  usedToday: "נוצלו",
  ofToday: "מתוך",
  aiUsesToday: "שימושי בינה היום (מתאפס בחצות UTC).",
  guest: "אורח",
  signedIn: "מחובר",
  aiUsesLeftToday: "שימושי בינה נותרו היום. מתאפס בחצות UTC.",
  guestModePrefix: "מצב אורח:",
  freeUsesPerDay: "שימושי בינה חינמיים ביום. התחברו ל-",
  perDay: "ביום.",
  aiUsesPerDay: "שימושי בינה ביום.",
  aiUseLeft: "שימוש בינה נותר",
  aiUsesLeft: "שימושי בינה נותרו",
  guestAiUses: "שימושי בינה לאורח ביום",
  ctrlEnter: "Ctrl+Enter",
  listTitle: "תרגול",
  blankSheet: "דף ריק",
  blankSheetBody:
    "כתבו את השאלה על הלוח, הדביקו אותה כטקסט (Ctrl+V), או הדביקו/העלו תמונה של דף עבודה, ואז פתרו.",
  questionHeader: "שאלה",
  difficultyHeader: "רמה",
  loadingQuestions: "טוען שאלות…",
  noQuestions: "אין עדיין שאלות תרגול בנושא הזה.",
  chooseLevel: "בחרו רמה",
  chooseSubject: "בחרו נושא",
  chooseExercise: "בחרו תרגיל",
  gradeFilter: "כיתה",
  allGrades: "הכל",
  gradeEmpty: "אין עדיין תרגילים לכיתה הזו.",
  newProblem: "תרגיל חדש",
  newProblemFailed: "לא הצלחנו ליצור תרגיל חדש.",
  relatedPractice: "תרגיל קשור",
  relatedPracticeFailed: "לא הצלחנו לפתוח תרגיל קשור.",
  stepDown: "שלוש טעויות. הנה דוגמה פתורה, ואחר כך תרגיל קל יותר.",
  stepPrereq:
    "שלוש טעויות. הנה דוגמה פתורה של מיומנות קודמת, ואחר כך תרגיל חדש.",
  stepUp: "שלוש הצלחות ברצף. התרגיל הבא קשה יותר בדרגה אחת.",
  generatorFailed: "לא הצלחנו לפתוח את התרגיל.",
  practiceThisSubject: "תרגול הנושא",
  curriculumLabel: "תכנית לימודים",
  backToLevels: "כל הרמות",
  backToSubjects: "כל הנושאים",
  emptyBand:
    "אין עדיין תרגילים מקטלוג ברמה הזו. התחילו תרגול מודרך ונפיק תרגילים.",
  pathPercent: (pct: number) => `${pct}% בקיאות`,
  subjectProficient: "בקיאות בנושא",
  chooseAnotherSubject: "נושא אחר",
  pathNext: "הבא במסלול",
  generatedPathStem: (skill: string, subject: string) =>
    `מסלול תרגול: ${skill || subject}`,
  pasteTheProblemTitle: "הדביקו את השאלה",
  pasteTheProblemHelp: "Ctrl+V לטקסט או לתמונת דף עבודה, או העלו תמונה.",
  pastePlaceholder: "או הקלידו את השאלה כאן…",
  start: "התחלה",
  clear: "ניקוי",
  uploadImage: "העלאת תמונה",
  readingWorksheet: "קורא את דף העבודה…",
  loadingQuestionPane: "טוען שאלה…",
  worksheetAlt: "דף עבודה",
  problem: "שאלה",
  hintFor: "רמז עבור",
  animatedExplanation: "הסבר מונפש",
  animatedExplanationHelp:
    "תזכורת קצרה למיומנות הזו. המספרים בדוגמה שונים מאלה שבשאלה.",
  watchExplanation: "צפו בהסבר",
  skillReminder: "איך זה עובד",
  skillReminderExample: "דוגמה",
  showShortExample: "הציגו דוגמה קצרה",
  loadingShortExample: "כותבים דוגמה…",
  dismissTip: "סגירת הרמז",
  dragToMove: "גררו כדי להזיז",
  practiceTooltip: "תרגול",
  help: "עזרה",
  changeProblem: "החלפת שאלה",
  orderTasks: "התחילו במשימות לפי הסדר. Enter מוסיף מרווח במשימה הנוכחית.",
  guestDailyLimit: (limit: number) =>
    `ניצלתם ${limit} שימושי בינה חינמיים להיום. התחברו למכסה גבוהה יותר.`,
  memberDailyLimit: (limit: number) =>
    `הגעתם למכסה היומית (${limit} שימושים). נסו שוב מחר.`,
  tutorFailed: "המורה לא הצליח לענות. נסו שוב.",
  imageUploadFailed: "העלאת התמונה נכשלה",
  questionMissing: "שאלת התרגול הזו לא קיימת.",
  notPracticeQuestion: "זו לא שאלת תרגול.",
  loadFailed: "לא הצלחנו לטעון את שאלת התרגול.",
};

export type PracticeUi = typeof UI_EN;

export function practiceUi(he: boolean): PracticeUi {
  return he ? UI_HE : UI_EN;
}

export function shellUi(he: boolean) {
  if (!he) {
    return {
      lessons: "Lessons",
      questions: "Questions",
      answers: "Answers",
      feedbackTip: "Send Feedback",
      feedback: "Feedback",
      classroom: "Classroom",
      signInTeacher: "Sign in as Teacher",
      signInStudent: "Sign in as Student",
      signOut: "Sign Out",
      onlineStudents: "Online students",
      onlineStudentsTip: "Online Students — who joined and board editing",
      studentsLabel: "Students",
      invite: "Invite",
      endLesson: "End lesson",
      reopenLesson: "Reopen lesson",
      endLessonConfirm:
        "Students will be disconnected, and the share link will not let anyone join until you reopen the lesson.",
      lessonEndedTitle: "This lesson has ended",
      lessonEndedBody:
        "Your teacher ended this lesson. You can join again if they reopen it.",
      selectLesson: "Please select a lesson",
      dismiss: "Dismiss",
      feedbackTitle: "Send Feedback",
      contactTitle: "Contact Us",
      privacy: "Privacy Policy",
      contact: "Contact Us",
    };
  }
  return {
    lessons: "שיעורים",
    questions: "שאלות",
    answers: "תשובות",
    feedbackTip: "שליחת משוב",
    feedback: "משוב",
    classroom: "כיתה",
    signInTeacher: "כניסה כמורה",
    signInStudent: "כניסה כתלמיד",
    signOut: "יציאה",
    onlineStudents: "תלמידים מקוונים",
    onlineStudentsTip: "תלמידים מקוונים — מי הצטרף ועריכת הלוח",
    studentsLabel: "תלמידים",
    invite: "הזמנה",
    endLesson: "סיום השיעור",
    reopenLesson: "פתיחה מחדש",
    endLessonConfirm:
      "התלמידים ינותקו, וקישור השיתוף לא יאפשר הצטרפות עד שתפתחו את השיעור מחדש.",
    lessonEndedTitle: "השיעור הסתיים",
    lessonEndedBody:
      "המורה סיים את השיעור. אפשר להצטרף שוב אם יפתחו אותו מחדש.",
    selectLesson: "בחרו שיעור",
    dismiss: "סגירה",
    feedbackTitle: "שליחת משוב",
    contactTitle: "יצירת קשר",
    privacy: "מדיניות פרטיות",
    contact: "יצירת קשר",
  };
}

const SITE_LINK_HE: Record<string, string> = {
  "/practice": "מורה למתמטיקה",
  "/practice/blank": "תרגול בדף ריק",
  "/registerTeacher": "למורים",
  "/registerStudent": "הרשמת תלמיד",
  "/": "איך זה עובד",
  "/login": "כניסה",
};

export function siteLinkLabel(path: string, english: string, he: boolean): string {
  if (!he) return english;
  return SITE_LINK_HE[path] ?? english;
}

export function welcomePage(he: boolean) {
  if (!he) {
    return {
      brandLine: WELCOME_PATHS.brandLine,
      brandSub: WELCOME_PATHS.brandSub,
      classroom: WELCOME_PATHS.classroom,
      teacherSteps: TEACHER_WORKFLOW_STEPS as readonly string[],
      bullets: [
        "Type math on the grid with your keyboard",
        "Select, move, and Ctrl+drag to duplicate work",
        "Lines, text boxes, and optional free sketch",
      ],
      featuresTitle: "Key Features",
      howLessonWorks: "How a live lesson works",
      allTutorials: "All video tutorials",
      siteAria: "Site links",
      classroomAria: "Classroom account",
    };
  }
  return {
    brandLine: "לוח מתמטיקה",
    brandSub: "שתי דרכים לאותו לוח: כיתה אונליין, או מורה בינה מלאכותית לתרגול עצמאי.",
    classroom: {
      title: "כיתה",
      teacherBlurb: "מורים ותלמידים יחד — מנהלים שיעור אונליין בארבעה שלבים:",
      studentBlurb:
        "מורים ותלמידים יחד — מצטרפים לשיעור משותף, ואז עובדים על שאלות ותשובות.",
      guestBlurb:
        "מורים ותלמידים יחד — שיעורים אונליין, שאלות ותשובות על לוח משותף.",
      ctaTeacher: "אל השיעורים",
      ctaStudent: "אל השיעורים",
      ctaGuest: "התחילו בכיתה",
      signInTeacher: "כניסה כמורה",
      signInStudent: "כניסה כתלמיד",
      walkthroughCta: "צפו בהדרכת הכיתה",
    },
    teacherSteps: [
      "צרו או פתחו שיעור מתוך השיעורים",
      "לחצו על הזמנה בסרגל העליון והעתיקו את קישור הגישה",
      "התלמידים מצטרפים דרך הקישור — פתחו את התלמידים המקוונים כדי לאפשר עריכה",
      "הוסיפו שאלות מתוך שאלות, ועברו על תשובות מתוך תשובות",
    ],
    bullets: [
      "הקלידו מתמטיקה במקלדת ישר לתאי הרשת",
      "בחירה, הזזה, ו-Ctrl+גרירה כדי לשכפל",
      "קווים, תיבות טקסט, ושרטוט חופשי כשצריך",
    ],
    featuresTitle: "יכולות עיקריות",
    howLessonWorks: "איך עובד שיעור אונליין",
    allTutorials: "כל סרטוני ההדרכה",
    siteAria: "קישורים באתר",
    classroomAria: "חשבון כיתה",
  };
}
